import type { ReactNode } from "react";

import styled from "styled-components";

import { useTranslate } from "@similarweb/i18n-provider";
import { Info24px, SWReactCountryIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Card, ListItemPlaceholderLoader, TitlePlaceholderLoader, Tooltip } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { useSelectorTrigger } from "app/components/filters-bar/useSelectorTrigger";

const StyledTitleLoader = styled(TitlePlaceholderLoader)`
    height: 24px;
`;

const StyledMainTitleLoader = styled(TitlePlaceholderLoader)`
    height: 40px;
`;

const StyledListItemLoader = styled(ListItemPlaceholderLoader)`
    height: 20px;
`;

type KpiCardQueryHeaderProps = {
    title: string;
    tooltip: string;
    dateRangeText: string;
    countryCode: number;
    countryText: string;
    selectorTriggerId: string;
    dataAutomationPrefix: string;
    badge?: ReactNode;
};

export const KpiCardQueryHeader = ({
    title,
    tooltip,
    dateRangeText,
    countryCode,
    countryText,
    selectorTriggerId,
    dataAutomationPrefix,
    badge,
}: KpiCardQueryHeaderProps) => {
    const { durationTriggerProps, countryTriggerProps } = useSelectorTrigger(selectorTriggerId);

    return (
        <Card.Header withDivider>
            <Card.Title>
                <Flex
                    gap="8px"
                    alignItems="center"
                >
                    <Text
                        fontVariant="h5_2"
                        data-automation={`${dataAutomationPrefix}-title`}
                    >
                        {title}
                    </Text>
                    {badge}
                    <Tooltip
                        content={tooltip}
                        data-automation={`${dataAutomationPrefix}-title-tooltip`}
                    >
                        <Info24px
                            size={24}
                            color={colorsPalettes.carbon[200]}
                            data-automation={`${dataAutomationPrefix}-title-info-icon`}
                        />
                    </Tooltip>
                </Flex>
                <Card.Query>
                    <Card.QueryItem
                        iconName="daily-ranking"
                        data-automation={`${dataAutomationPrefix}-date-range`}
                        {...durationTriggerProps}
                    >
                        {dateRangeText}
                    </Card.QueryItem>
                    <Flex
                        gap="8px"
                        alignItems="center"
                        data-automation={`${dataAutomationPrefix}-country`}
                        {...countryTriggerProps}
                    >
                        <SWReactCountryIcons
                            countryCode={countryCode}
                            size="xs"
                        />
                        <Text
                            fontVariant="chip1"
                            color={colorsPalettes.carbon[400]}
                        >
                            {countryText}
                        </Text>
                    </Flex>
                </Card.Query>
            </Card.Title>
        </Card.Header>
    );
};

type KpiCardLoadingProps = {
    dataAutomation: string;
    bodyVariant?: "lines" | "metric";
};

export const KpiCardLoading = ({ dataAutomation, bodyVariant = "lines" }: KpiCardLoadingProps) => (
    <Card data-automation={dataAutomation}>
        <Card.Header>
            <Card.Title>
                <StyledTitleLoader />
            </Card.Title>
        </Card.Header>
        <Flex
            flexDirection="column"
            justifyContent="center"
            padding="16px 24px"
            gap={bodyVariant === "metric" ? "8px" : "12px"}
            flex="1"
        >
            {bodyVariant === "metric" ? <StyledMainTitleLoader /> : <StyledListItemLoader />}
            <StyledListItemLoader />
        </Flex>
    </Card>
);

type KpiCardStatusBodyProps = {
    title: string;
    dataAutomation?: string;
};

export const KpiCardStatusBody = ({ title, dataAutomation }: KpiCardStatusBodyProps) => {
    const { translate } = useTranslate();

    return (
        <Flex
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            textAlign="center"
            flex="1"
            minHeight={0}
            padding="16px 24px"
            data-automation={dataAutomation}
        >
            <Text
                fontVariant="body2"
                color={colorsPalettes.carbon[500]}
            >
                {translate(title)}
            </Text>
        </Flex>
    );
};
